import React, { useState, useEffect, useMemo } from 'react';
import { X, AlertCircle, CheckCircle, Search, Save, Filter, Loader2, ChevronsUpDown } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import vendorApi from '../../../api/vendorApi';
import { VendorAlias, UpdateVendorAliasRequest, VendorAccountData, StandardAccount } from '../../../api/types';
import formatCurrency from '../../../utils/formatCurrency';
import StandardAccountSelector from '../components/StandardAccountSelector';

interface VendorManagementModalProps {
    isOpen: boolean;
    onClose: () => void;
    businessId?: string;
}

const VendorManagementModal: React.FC<VendorManagementModalProps> = ({ isOpen, onClose, businessId }) => {
    const queryClient = useQueryClient();
    const [selectedTab, setSelectedTab] = useState<'awaiting_approval' | 'needs_review' | 'verified'>('awaiting_approval');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [selectedAliases, setSelectedAliases] = useState<Set<string>>(new Set());
    const [page, setPage] = useState(1);
    const pageSize = 10;

    const handleSort = (column: string) => {
        if (sortBy === column) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortOrder('asc');
        }
    };

    // State for editing vendor name
    const [editingVendorId, setEditingVendorId] = useState<string | null>(null);
    const [editVendorName, setEditVendorName] = useState('');

    // State for filtering and sorting
    const [sortBy, setSortBy] = useState<string>('clean_vendor_name');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
    const [directionFilter, setDirectionFilter] = useState<string>('');
    const [fiscalFilter, setFiscalFilter] = useState('');

    // State for account selector
    const [accountSelectorOpen, setAccountSelectorOpen] = useState(false);
    const [activeAliasId, setActiveAliasId] = useState<string | null>(null);

    // Fetch Standard Accounts for Fiscal Filter
    const { data: standardAccounts } = useQuery({
        queryKey: ['standardAccounts', businessId],
        queryFn: () => vendorApi.getStandardAccounts(businessId),
        staleTime: 5 * 60 * 1000,
    });

    const uniqueFiscalBuckets = useMemo(() => {
        if (!standardAccounts?.items) return [];
        const buckets = new Set(standardAccounts.items.map(a => a.fiscal_bucket).filter(Boolean));
        return Array.from(buckets).sort();
    }, [standardAccounts]);

    // Debounce search term & fiscal filter
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    // Fetch aliases
    const { data, isLoading, isError } = useQuery({
        queryKey: ['vendorAliases', selectedTab, debouncedSearchTerm, page, sortBy, sortOrder, directionFilter, fiscalFilter, businessId],
        queryFn: () => vendorApi.getAliases(
            selectedTab,
            debouncedSearchTerm,
            page,
            pageSize,
            sortBy,
            sortOrder,
            directionFilter,
            fiscalFilter,
            businessId
        ),
        enabled: isOpen,
    });

    // Reset page on filter change
    useEffect(() => {
        setPage(1);
    }, [selectedTab, debouncedSearchTerm, directionFilter, fiscalFilter]);

    // Reset selection on tab change
    useEffect(() => {
        setSelectedAliases(new Set());
        setPage(1);
        setEditingVendorId(null);
    }, [selectedTab, debouncedSearchTerm]);

    const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked && data?.items) {
            const allIds = data.items.map(a => a._id);
            setSelectedAliases(new Set(allIds));
        } else {
            setSelectedAliases(new Set());
        }
    };

    const handleSelectOne = (id: string) => {
        const newSelected = new Set(selectedAliases);
        if (newSelected.has(id)) {
            newSelected.delete(id);
        } else {
            newSelected.add(id);
        }
        setSelectedAliases(newSelected);
    };

    // Batch Update Mutation
    const batchUpdateMutation = useMutation({
        mutationFn: ({ ids, updates }: { ids: string[], updates: UpdateVendorAliasRequest }) =>
            vendorApi.batchUpdateAliases(ids, updates, businessId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['vendorAliases'] });
            setSelectedAliases(new Set());
        }
    });

    // Single Update Mutation
    const updateMutation = useMutation({
        mutationFn: ({ id, updates }: { id: string, updates: UpdateVendorAliasRequest }) =>
            vendorApi.updateAlias(id, updates, businessId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['vendorAliases'] });
            setEditingVendorId(null);
        }
    });

    const handleBatchVerify = () => {
        if (selectedAliases.size === 0) return;
        if (window.confirm(`Approve ${selectedAliases.size} selected vendors?`)) {
            batchUpdateMutation.mutate({
                ids: Array.from(selectedAliases),
                updates: { resolution_status: 'verified' }
            });
        }
    };

    const handleStatusToggle = (id: string, currentStatus: string | undefined) => {
        const newStatus = currentStatus === 'verified' ? 'needs_review' : 'verified';
        updateMutation.mutate({
            id,
            updates: { resolution_status: newStatus }
        });
    };

    const startEditingName = (currentName: string, id: string) => {
        setEditVendorName(currentName);
        setEditingVendorId(id);
    };

    const saveVendorName = (id: string) => {
        if (!editVendorName.trim()) return;
        updateMutation.mutate({
            id,
            updates: { clean_vendor_name: editVendorName }
        });
    };

    const handleAccountChange = (account: StandardAccount) => {
        if (!activeAliasId) return;

        const displayName = account.display_name || account.account_name;
        const newFinzData: VendorAccountData = {
            account_id: account._id,
            account_number: account.account_number,
            account_name: displayName,
            fiscal_bucket: account.fiscal_bucket,
        };

        // Optimistically verify when mapping changes
        updateMutation.mutate({
            id: activeAliasId,
            updates: {
                finz_data: newFinzData,
                resolution_status: 'verified'
            }
        });

        setAccountSelectorOpen(false);
        setActiveAliasId(null);
    };

    const openAccountSelector = (aliasId: string) => {
        setActiveAliasId(aliasId);
        setAccountSelectorOpen(true);
    };

    // Lock body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[60] overflow-hidden" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />
                <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex sm:pl-16 pointer-events-none">
                    {/* Increased width to full screen */}
                    <div className="w-screen max-w-7xl transform transition-all ease-in-out duration-500 sm:duration-700 h-full flex flex-col bg-white shadow-xl pointer-events-auto">

                        {/* Header */}
                        <div className="px-4 py-6 sm:px-6 border-b border-gray-200">
                            <div className="flex items-start justify-between">
                                <h2 className="text-lg font-medium text-gray-900">Manage Vendor Aliases</h2>
                                <button type="button" className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none" onClick={onClose}>
                                    <span className="sr-only">Close panel</span>
                                    <X className="h-6 w-6" />
                                </button>
                            </div>

                            {/* Tabs */}
                            <div className="mt-4 border-b border-gray-200">
                                <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                                    {['awaiting_approval', 'needs_review', 'verified'].map((tab) => (
                                        <button
                                            key={tab}
                                            onClick={() => setSelectedTab(tab as any)}
                                            className={`${selectedTab === tab
                                                ? 'border-indigo-500 text-indigo-600'
                                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm capitalize`}
                                        >
                                            {tab.replace('_', ' ')}
                                        </button>
                                    ))}
                                </nav>
                            </div>

                            {/* Filters & Batch Actions */}
                            <div className="mt-4 flex items-center justify-between gap-4">
                                <div className="flex items-center gap-3 flex-1">
                                    <div className="relative max-w-xs flex-1">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Search className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="text"
                                            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                            placeholder="Search by name"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>

                                    <select
                                        value={directionFilter}
                                        onChange={(e) => setDirectionFilter(e.target.value)}
                                        className="block w-40 pl-3 pr-10 py-2 text-base border-gray-300 border focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                                    >
                                        <option value="">All Directions</option>
                                        <option value="inflow">Inflow</option>
                                        <option value="outflow">Outflow</option>
                                    </select>

                                    <select
                                        value={fiscalFilter}
                                        onChange={(e) => setFiscalFilter(e.target.value)}
                                        className="block w-48 pl-3 pr-10 py-2 text-base border-gray-300 border focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                                    >
                                        <option value="">All Buckets</option>
                                        <option value="Uncategorized">Uncategorized</option>
                                        {uniqueFiscalBuckets.map(bucket => (
                                            <option key={bucket} value={bucket}>{bucket}</option>
                                        ))}
                                    </select>
                                </div>

                                {selectedAliases.size > 0 && (
                                    <div className="flex items-center gap-2 bg-indigo-50 px-3 py-1.5 rounded-md border border-indigo-100">
                                        <span className="text-sm text-indigo-700 font-medium">{selectedAliases.size} selected</span>
                                        <div className="h-4 w-px bg-indigo-200 mx-1" />
                                        <button
                                            onClick={handleBatchVerify}
                                            disabled={batchUpdateMutation.isPending}
                                            className="text-sm font-medium text-indigo-700 hover:text-indigo-900 flex items-center gap-1"
                                        >
                                            {batchUpdateMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3" />}
                                            Approve Selected
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 flex flex-col overflow-y-auto min-h-0 bg-gray-50">
                            {isLoading ? (
                                <div className="flex-1 flex items-center justify-center">
                                    <Loader2 className="h-8 w-8 text-indigo-500 animate-spin" />
                                </div>
                            ) : isError ? (
                                <div className="p-8 text-center text-red-500">Failed to load vendors.</div>
                            ) : (
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50 sticky top-0 z-10">
                                        <tr>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                                                <input
                                                    type="checkbox"
                                                    className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                                    onChange={handleSelectAll}
                                                    checked={selectedAliases.size > 0 && selectedAliases.size === (data?.items.length || 0)}
                                                />
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 group"
                                                onClick={() => handleSort('clean_vendor_name')}
                                            >
                                                <div className="flex items-center gap-1">
                                                    Vendor Info
                                                    {sortBy === 'clean_vendor_name' && (
                                                        <span className="text-indigo-500">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                                                    )}
                                                </div>
                                            </th>
                                            {/* New Direction Column */}
                                            <th
                                                scope="col"
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24 cursor-pointer hover:bg-gray-100 group"
                                                onClick={() => handleSort('direction')}
                                            >
                                                <div className="flex items-center gap-1">
                                                    Direction
                                                    {sortBy === 'direction' && (
                                                        <span className="text-indigo-500">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                                                    )}
                                                </div>
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 group"
                                                onClick={() => handleSort('finz_data.account_name')}
                                            >
                                                <div className="flex items-center gap-1">
                                                    Finz Account Mapping
                                                    {sortBy === 'finz_data.account_name' && (
                                                        <span className="text-indigo-500">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                                                    )}
                                                </div>
                                            </th>
                                            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {data?.items.map((alias) => (
                                            <tr key={alias._id} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-6 py-2 whitespace-nowrap">
                                                    <input
                                                        type="checkbox"
                                                        className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                                        checked={selectedAliases.has(alias._id)}
                                                        onChange={() => handleSelectOne(alias._id)}
                                                    />
                                                </td>
                                                <td className="px-6 py-2">
                                                    <div className="flex items-center gap-2">
                                                        {editingVendorId === alias._id ? (
                                                            <div className="flex items-center gap-1 flex-1">
                                                                <input
                                                                    type="text"
                                                                    value={editVendorName}
                                                                    onChange={(e) => setEditVendorName(e.target.value)}
                                                                    className="block w-full px-2 py-1 text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                                                                    autoFocus
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter') saveVendorName(alias._id);
                                                                        if (e.key === 'Escape') setEditingVendorId(null);
                                                                    }}
                                                                />
                                                                <button
                                                                    onClick={() => saveVendorName(alias._id)}
                                                                    className="p-1 text-green-600 hover:text-green-800"
                                                                >
                                                                    <CheckCircle className="h-4 w-4" />
                                                                </button>
                                                                <button
                                                                    onClick={() => setEditingVendorId(null)}
                                                                    className="p-1 text-red-600 hover:text-red-800"
                                                                >
                                                                    <X className="h-4 w-4" />
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <div
                                                                className="flex items-center gap-2 group overflow-hidden cursor-pointer hover:bg-gray-100 p-1 -m-1 rounded transition-colors"
                                                                onClick={() => startEditingName(alias.clean_vendor_name, alias._id)}
                                                            >
                                                                <span className="text-sm font-medium text-gray-900 whitespace-nowrap">{alias.clean_vendor_name}</span>
                                                                <span className="text-xs text-gray-400 font-mono truncate max-w-[200px]" title={alias.raw_norm_name}>
                                                                    ({alias.raw_norm_name})
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                {/* Direction Column */}
                                                <td className="px-6 py-2 whitespace-nowrap">
                                                    <span className={`text-[10px] uppercase font-bold inline-flex px-1.5 py-0.5 rounded ${alias.direction === 'inflow' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                        {alias.direction}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-2 cursor-pointer group" onClick={() => openAccountSelector(alias._id)}>
                                                    {alias.finz_data ? (
                                                        <div className="flex items-center gap-2 p-1.5 -ml-1.5 rounded-md group-hover:bg-indigo-50 transition-colors">
                                                            <span className="text-xs text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-100 group-hover:bg-white whitespace-nowrap">
                                                                {alias.finz_data.fiscal_bucket || 'Uncategorized'}
                                                            </span>
                                                            {alias.finz_data.fiscal_bucket === 'INTERNAL TRANSFER' ? (
                                                                <span className="text-sm text-gray-900 font-mono whitespace-nowrap truncate max-w-[250px]" title={alias.finz_data.linked_transfer_id}>
                                                                    linked_transfer_id: {alias.finz_data.linked_transfer_id || 'N/A'}
                                                                </span>
                                                            ) : (
                                                                <>
                                                                    <span className="text-sm text-gray-900 font-medium whitespace-nowrap">{alias.finz_data.account_name}</span>
                                                                    <span className="text-xs text-gray-400 font-mono whitespace-nowrap">#{alias.finz_data.account_number}</span>
                                                                </>
                                                            )}
                                                            <ChevronsUpDown className="h-3 w-3 text-gray-400 group-hover:text-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                                                        </div>
                                                    ) : (
                                                        <button className="text-sm text-indigo-600 hover:text-indigo-800 font-medium">
                                                            + Select Account
                                                        </button>
                                                    )}
                                                </td>
                                                <td className="px-6 py-2 whitespace-nowrap text-right">
                                                    <button
                                                        onClick={() => handleStatusToggle(alias._id, alias.resolution_status)}
                                                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${alias.resolution_status === 'verified'
                                                            ? 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200 hover:border-green-300'
                                                            : alias.resolution_status === 'needs_review'
                                                                ? 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100 hover:border-red-300'
                                                                : 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100 hover:border-orange-300'
                                                            }`}
                                                    >
                                                        {alias.resolution_status === 'verified' ? <AlertCircle className="h-3 w-3" /> : <CheckCircle className="h-3 w-3" />}
                                                        {alias.resolution_status === 'verified' ? 'Needs Review' : 'Verify'}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                        {(!data?.items || data.items.length === 0) && (
                                            <tr>
                                                <td colSpan={5} className="px-6 py-12 text-center text-sm text-gray-500">
                                                    No vendors found with status "{selectedTab.replace('_', ' ')}".
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {/* Footer (Pagination if needed, currently scroll) */}
                        <div className="px-4 py-4 sm:px-6 border-t border-gray-200 bg-gray-50 flex justify-between items-center text-xs text-gray-500">
                            <span>Showing {data?.items.length || 0} of {data?.total || 0} items</span>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                    className="px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                                >
                                    Previous
                                </button>
                                <span className="px-3 py-1 bg-white border border-gray-300 rounded min-w-[30px] text-center">
                                    {page}
                                </span>
                                <button
                                    onClick={() => setPage(p => p + 1)}
                                    disabled={!data || (page * pageSize) >= data.total}
                                    className="px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <StandardAccountSelector
                isOpen={accountSelectorOpen}
                onClose={() => setAccountSelectorOpen(false)}
                onSelect={handleAccountChange}
                currentAccountId={undefined} // Can pass if needed, but we rely on activeAliasId context
                businessId={businessId}
            />
        </div>
    );
};

export default VendorManagementModal;
